import { AnimatedSection } from '@denqfabrik/landing-ui';
import { useState } from 'react';
import { useTranslation } from '../../i18n';

type Tab = 'claude-code' | 'cursor' | 'codex';

const TABS: { id: Tab; label: string; badge?: string }[] = [
  { id: 'claude-code', label: 'Claude Code', badge: 'Hooks' },
  { id: 'cursor', label: 'Cursor', badge: 'Rules' },
  { id: 'codex', label: 'Codex', badge: 'AGENTS.md' },
];

const SNIPPETS: Record<Tab, { file: string; language: string; code: string; note: string; noteEn: string }> = {
  'claude-code': {
    file: '.claude/settings.json',
    language: 'json',
    code: `{
  "hooks": {
    "PostToolUse": [
      {
        "matcher": "Edit|Write|MultiEdit",
        "hooks": [{
          "type": "command",
          "command": "aletiq --verify"
        }]
      }
    ]
  }
}`,
    note: 'Deterministisch: Aletiq wird nach jeder Code-Änderung aufgerufen. Nicht überspringbar.',
    noteEn: 'Deterministic: Aletiq is called after every code change. Cannot be skipped.',
  },
  cursor: {
    file: '.cursor/rules/aletiq.mdc',
    language: 'markdown',
    code: `---
description: Aletiq verification after changes
globs: **/*.{ts,tsx,js,jsx,html,css}
---

After modifying any file, ALWAYS run:
\`aletiq --verify "check recent changes"\`

Never skip verification. Re-runs are free.`,
    note: 'Soft Rule: Das Modell folgt der Anweisung — kann sie aber in Ausnahmen ignorieren.',
    noteEn: 'Soft rule: The model follows the instruction — but may skip it in edge cases.',
  },
  codex: {
    file: 'AGENTS.md',
    language: 'markdown',
    code: `## Verification

After every code change, run:
\`\`\`
aletiq --verify "check recent changes"
\`\`\`

This is mandatory. Re-runs are free.
Do not skip verification.`,
    note: 'Harte Regel in AGENTS.md: Codex behandelt dies als verbindliche Anweisung.',
    noteEn: 'Hard rule in AGENTS.md: Codex treats this as a binding instruction.',
  },
};

export default function AppWorksWithSection() {
  const { t, lang } = useTranslation();
  const isEnglish = lang === 'en';
  const [activeTab, setActiveTab] = useState<Tab>('claude-code');
  const snippet = SNIPPETS[activeTab];

  return (
    <section id="works-with" data-nav-theme="light" className="bg-[#f2f5fa] py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <AnimatedSection>
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-teal-500">
              {t.worksWith.subtitle}
            </p>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
              {t.worksWith.heading}{' '}
              <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                {t.worksWith.headingHighlight}
              </span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              {t.worksWith.description}
            </p>
          </div>
        </AnimatedSection>

        {/* Setup Tabs + Code Snippet */}
        <AnimatedSection delay={100}>
          <div className="mx-auto max-w-2xl">
            {/* Tab bar */}
            <div className="mb-4 flex gap-2 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {tab.label}
                  {tab.badge && (
                    <span
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                        activeTab === tab.id
                          ? 'bg-teal-400/20 text-teal-300'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Code block */}
            <div className="overflow-hidden rounded-2xl border border-[#dce2eb] bg-[#0d1117] shadow-lg">
              {/* File header */}
              <div className="flex items-center justify-between border-b border-slate-700/50 px-4 py-2.5">
                <span className="font-mono text-xs text-slate-400">{snippet.file}</span>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(snippet.code)}
                  className="rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-200"
                >
                  {isEnglish ? 'Copy' : 'Kopieren'}
                </button>
              </div>

              {/* Code */}
              <pre className="overflow-x-auto p-4 sm:p-5">
                <code className="block font-mono text-xs leading-relaxed text-slate-300 sm:text-[13px]">
                  {snippet.code}
                </code>
              </pre>

              {/* Note */}
              <div className="border-t border-slate-700/50 px-4 py-2.5">
                <p className="text-xs text-slate-500">
                  {activeTab === 'claude-code' && '🔒 '}
                  {activeTab !== 'claude-code' && '📝 '}
                  {isEnglish ? snippet.noteEn : snippet.note}
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Logo row — smaller, secondary */}
        <AnimatedSection delay={200}>
          <div className="mx-auto mt-12 flex flex-wrap items-center justify-center gap-4">
            {t.worksWith.agents.map((agent) => (
              <div
                key={agent.name}
                className="flex items-center gap-2 rounded-full border border-[#dce2eb] bg-white px-4 py-2"
              >
                {agent.logo && (
                  <img src={agent.logo} alt="" className="h-5 w-5" loading="lazy" />
                )}
                <span className="text-xs font-medium text-slate-600">{agent.name}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
